import type { Landmark } from '../../../data/types';
import type { Ring, Vec2, Vec3 } from '../../../geo/types';
import { ensureCCW } from '../../../geo/polygon';
import type { Frame, MeshBuilder, VertexStyle } from '../../mesh/meshBuilder';
import { SURFACE } from '../../mesh/attributes';
import { horizontalCap, orientedFace, triangulateRing } from '../solids';
import { gableRoof } from '../roofSolids';
import { panelPoint } from '../panels';
import { edgeFace, type MasonryStyles } from './masonry';
import { wallFace } from './wallCoords';
import { meshFrameOf, planBounds, planFrameOf, spanOver, toLayout } from './planFrame';

/** The labelled 1544 view and its archive analysis identify a higher front body with an
 * adjoining lower rear body. The 1538 painting also shows unequal roofs here. All dimensions,
 * unseen openings and the fitted orientation inside the retained reservation are estimates. */
export const SCAFFERIJ = {
  joinU: 4,
  rearHalfWidth: 4.4,
  frontEaves: 10,
  frontRidge: 15.3,
  rearEaves: 6.4,
  rearRidge: 10.6,
  windowDepth: 0.25,
  doorDepth: 1.0,
} as const;

interface Opening {
  readonly x0: number;
  readonly x1: number;
  readonly y0: number;
  readonly y1: number;
  readonly door?: boolean;
}

export interface ScafferijStyles extends MasonryStyles {
  readonly timber?: VertexStyle;
}

/** Two connected bodies within the original 24 × 10 m source rectangle. */
export function scafferijPlan(landmark: Landmark) {
  const plan = planFrameOf(landmark.footprint, landmark.heading);
  const bounds = planBounds(plan);
  // Source vertices are rounded to 10 cm, so the oriented bounding box is not necessarily
  // contained by the resulting quadrilateral. Fit its actual common cross-section instead.
  const u0 = bounds.u0 + 0.08;
  const u1 = bounds.u1 - 0.08;
  const span = spanOver(plan, u0, u1, (bounds.v0 + bounds.v1) / 2);
  if (!span) throw new Error(`${landmark.id}: no connected workshop footprint`);
  const b = { u0, u1, v0: span[0], v1: span[1] };
  const uc = (b.u0 + b.u1) / 2;
  const vc = (b.v0 + b.v1) / 2;
  const su = (b.u1 - b.u0) / 24;
  const sv = (b.v1 - b.v0) / 10;
  const joinU = uc + SCAFFERIJ.joinU * su;
  const rearV0 = vc - SCAFFERIJ.rearHalfWidth * sv;
  const rearV1 = vc + SCAFFERIJ.rearHalfWidth * sv;
  const outline: Ring = [
    [b.u0, rearV0],
    [joinU, rearV0],
    [joinU, b.v0],
    [b.u1, b.v0],
    [b.u1, b.v1],
    [joinU, b.v1],
    [joinU, rearV1],
    [b.u0, rearV1],
  ];
  return { plan, frame: meshFrameOf(plan), b, joinU, rearV0, rearV1, outline };
}

function openingRing(o: Opening): Ring {
  return [
    [o.x0, o.y0],
    [o.x1, o.y0],
    [o.x1, o.y1],
    [o.x0, o.y1],
  ];
}

/** Earcut can leave a long triangle edge across multiple aligned aperture corners.
 * Split those edges using their original coordinates so the facade and its reveals share
 * exact edges, rather than concealing a T junction with a looser closure tolerance. */
function conformTriangle(tri: readonly Vec2[], vertices: readonly Vec2[]): Vec2[][] {
  const boundary: Vec2[] = [];
  tri.forEach((a, i) => {
    const z = tri[(i + 1) % tri.length]!;
    const dx = z[0] - a[0],
      dy = z[1] - a[1];
    const length2 = dx * dx + dy * dy;
    const cuts = vertices
      .map((p) => ({ p, t: ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / length2 }))
      .filter(
        ({ p, t }) =>
          t > 1e-8 && t < 1 - 1e-8 && Math.abs(dx * (p[1] - a[1]) - dy * (p[0] - a[0])) < 1e-8
      )
      .sort((p, q) => p.t - q.t);
    boundary.push(a);
    let previous = -1;
    for (const { p, t } of cuts)
      if (t - previous > 1e-8) {
        boundary.push(p);
        previous = t;
      }
  });
  if (boundary.length === 3) return [[...tri]];
  const centre: Vec2 = [
    tri.reduce((s, p) => s + p[0], 0) / 3,
    tri.reduce((s, p) => s + p[1], 0) / 3,
  ];
  return boundary.map((a, i) => [centre, a, boundary[(i + 1) % boundary.length]!]);
}

/** A cut wall face with masonry jambs, heads and sills. The wide workshop door sits a
 * full metre into its entrance, so its shadow is a physical recess rather than a dark patch. */
function facade(
  builder: MeshBuilder,
  styles: ScafferijStyles,
  frame: Frame,
  from: Vec2,
  to: Vec2,
  outline: Ring,
  openings: readonly Opening[]
): void {
  const face = edgeFace(from, to);
  const coords = wallFace(styles.wall, frame, face.panel, outline);
  const point = (x: number, y: number, depth = 0): Vec3 => {
    const p = panelPoint(face.panel, x, y);
    return [p[0] - face.outward[0] * depth, p[1], p[2] - face.outward[2] * depth];
  };
  const holes = openings.map(openingRing);
  const vertices = [...outline, ...holes.flat()];
  for (const tri of triangulateRing(ensureCCW(outline), holes))
    for (const piece of conformTriangle(tri, vertices))
      orientedFace(
        builder,
        coords.style,
        frame,
        piece.map(([x, y]) => point(x, y)),
        face.outward,
        coords.uv
      );
  for (const o of openings) {
    const depth = o.door ? SCAFFERIJ.doorDepth : SCAFFERIJ.windowDepth;
    const ring = openingRing(o);
    const backStyle: VertexStyle = o.door
      ? (styles.timber ?? {
          ...styles.wall,
          surface: SURFACE.wood,
          color: styles.wall.color.map((c) => c * 0.28) as [number, number, number],
        })
      : styles.opening;
    orientedFace(
      builder,
      backStyle,
      frame,
      ring.map(([x, y]) => point(x, y, depth)),
      face.outward,
      coords.uv
    );
    const centre = point((o.x0 + o.x1) / 2, (o.y0 + o.y1) / 2);
    ring.forEach(([x, y], i) => {
      const [qx, qy] = ring[(i + 1) % ring.length]!;
      const middle = point((x + qx) / 2, (y + qy) / 2);
      const into: Vec3 = [centre[0] - middle[0], centre[1] - middle[1], centre[2] - middle[2]];
      orientedFace(
        builder,
        styles.glazing?.reveal ?? styles.wall,
        frame,
        [point(x, y), point(qx, qy), point(qx, qy, depth), point(x, y, depth)],
        into
      );
    });
  }
}

function wallOutline(width: number, eaves: number, ridge?: number): Ring {
  return ridge === undefined
    ? [
        [0, 0],
        [width, 0],
        [width, eaves],
        [0, eaves],
      ]
    : [
        [0, 0],
        [width, 0],
        [width, eaves],
        [width / 2, ridge],
        [0, eaves],
      ];
}

function windows(width: number, rows: readonly number[], count: number): Opening[] {
  return rows.flatMap((y) =>
    Array.from({ length: count }, (_, i) => {
      const x = (width * (i + 0.5)) / count;
      return { x0: x - 0.45, x1: x + 0.45, y0: y, y1: y + 1.45 };
    })
  );
}

/** Medieval boat-building workshop, without the taller later brewery or invented equipment. */
export function buildScafferij(
  builder: MeshBuilder,
  landmark: Landmark,
  styles: ScafferijStyles
): void {
  const p = scafferijPlan(landmark);
  const { b, joinU, rearV0, rearV1 } = p;
  const fullWidth = b.v1 - b.v0;
  const rearWidth = rearV1 - rearV0;
  const frontLength = b.u1 - joinU;
  const rearLength = joinU - b.u0;
  const frontOpenings: Opening[] = [
    { x0: fullWidth / 2 - 1.6, x1: fullWidth / 2 + 1.6, y0: 0.12, y1: 3.6, door: true },
    ...windows(fullWidth, [5.3, 8.0], 2),
    { x0: fullWidth / 2 - 0.4, x1: fullWidth / 2 + 0.4, y0: 10.8, y1: 12.0 },
  ];
  facade(
    builder,
    styles,
    p.frame,
    [b.u1, b.v0],
    [b.u1, b.v1],
    wallOutline(fullWidth, SCAFFERIJ.frontEaves, SCAFFERIJ.frontRidge),
    frontOpenings
  );
  for (const [a, z] of [
    [
      [joinU, b.v0],
      [b.u1, b.v0],
    ],
    [
      [b.u1, b.v1],
      [joinU, b.v1],
    ],
  ] as const)
    facade(
      builder,
      styles,
      p.frame,
      a,
      z,
      wallOutline(frontLength, SCAFFERIJ.frontEaves),
      windows(frontLength, [2.0, 5.3], 2)
    );

  // The taller body's back is exposed only above/beside the joined rear roof: no internal
  // duplicate wall or roof-end panel seals the connection between the two masses.
  const shoulder = (fullWidth - rearWidth) / 2;
  const backOutline: Ring = [
    [0, 0],
    [shoulder, 0],
    [shoulder, SCAFFERIJ.rearEaves],
    [fullWidth / 2, SCAFFERIJ.rearRidge],
    [fullWidth - shoulder, SCAFFERIJ.rearEaves],
    [fullWidth - shoulder, 0],
    [fullWidth, 0],
    [fullWidth, SCAFFERIJ.frontEaves],
    [fullWidth / 2, SCAFFERIJ.frontRidge],
    [0, SCAFFERIJ.frontEaves],
  ];
  facade(builder, styles, p.frame, [joinU, b.v1], [joinU, b.v0], backOutline, [
    { x0: fullWidth / 2 - 0.4, x1: fullWidth / 2 + 0.4, y0: 12.0, y1: 13.0 },
  ]);

  facade(
    builder,
    styles,
    p.frame,
    [b.u0, rearV1],
    [b.u0, rearV0],
    wallOutline(rearWidth, SCAFFERIJ.rearEaves, SCAFFERIJ.rearRidge),
    [
      { x0: rearWidth / 2 - 0.75, x1: rearWidth / 2 + 0.75, y0: 0.12, y1: 2.5, door: true },
      ...windows(rearWidth, [4.2], 2),
    ]
  );
  for (const [a, z] of [
    [
      [b.u0, rearV0],
      [joinU, rearV0],
    ],
    [
      [joinU, rearV1],
      [b.u0, rearV1],
    ],
  ] as const)
    facade(
      builder,
      styles,
      p.frame,
      a,
      z,
      wallOutline(rearLength, SCAFFERIJ.rearEaves),
      windows(rearLength, [2.0], 4)
    );

  gableRoof(builder, styles, p.frame, {
    u0: joinU,
    u1: b.u1,
    v0: b.v0,
    v1: b.v1,
    eaveY: SCAFFERIJ.frontEaves,
    ridgeY: SCAFFERIJ.frontRidge,
    ends: { start: false, end: false },
  });
  gableRoof(builder, styles, p.frame, {
    u0: b.u0,
    u1: joinU,
    v0: rearV0,
    v1: rearV1,
    eaveY: SCAFFERIJ.rearEaves,
    ridgeY: SCAFFERIJ.rearRidge,
    ends: { start: false, end: false },
  });
  horizontalCap(builder, styles.wall, p.frame, p.outline, 0, false);
}

/** A closed door still blocks the workshop interior, but its deep entrance remains approachable.
 * Collision follows the joined body outline and both physical recesses, not the reservation box. */
export function scafferijObstacles(landmark: Landmark): readonly Ring[] {
  const p = scafferijPlan(landmark);
  const { b, joinU, rearV0, rearV1 } = p;
  const vc = (b.v0 + b.v1) / 2;
  const ring: Ring = [
    [b.u0, rearV0],
    [joinU, rearV0],
    [joinU, b.v0],
    [b.u1, b.v0],
    [b.u1, vc - 1.6],
    [b.u1 - SCAFFERIJ.doorDepth, vc - 1.6],
    [b.u1 - SCAFFERIJ.doorDepth, vc + 1.6],
    [b.u1, vc + 1.6],
    [b.u1, b.v1],
    [joinU, b.v1],
    [joinU, rearV1],
    [b.u0, rearV1],
    [b.u0, vc + 0.75],
    [b.u0 + SCAFFERIJ.doorDepth, vc + 0.75],
    [b.u0 + SCAFFERIJ.doorDepth, vc - 0.75],
    [b.u0, vc - 0.75],
  ];
  return [ring.map((q) => toLayout(p.plan, q))];
}
